import { html, LonaWebComponent } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { TermRow } from "./term-row";

// A terminal frame, painted: the still-image counterpart to the session
// replay's motion.
//
// The view model is deliberately the wire shape a pane already produces —
// tsh's render boundary is one finished ANSI string per row (PaneSlot.lines
// in tento-tui-core, the return of Pane::render), so a caller holding a
// captured frame binds it verbatim: no re-encoding, no per-row markup, no
// authoring format. That is the whole fidelity argument: this component
// paints the bytes the terminal painted.
//
// What it does NOT do, on purpose:
//
//   - No motion. Rows appear at bind, all at once. Replaying is the session
//     replay's job, and it builds its rows through the same TermRow painter,
//     so the two stay pixel-consistent by construction.
//   - No scrolling machinery. A frame is finite — a pane rect's worth of
//     rows — and a figure that scrolls inside itself needs the replay's
//     focus/overflow contract, not a second copy of it. Wide content clips at
//     the box like a terminal does; a caller wanting a scrollbar sets
//     overflow-x on the host.
//   - No palette, no face, no size. Everything inherits, exactly like the
//     replay: the design showing a terminal owns what the terminal looks
//     like.
@component({ name: "std-term-screen" })
export class TermScreen extends LonaWebComponent {
  private $screen: HTMLElement = this.$("screen");

  static makeWith(vm: TermScreen.ViewModel): TermScreen {
    const $s = TermScreen.make() as TermScreen;
    $s.bind(vm);
    return $s;
  }

  bind(vm: TermScreen.ViewModel): void {
    this.$screen.replaceChildren(
      ...vm.lines.map((raw) => TermRow.$row(TermRow.parse(raw))),
    );
  }

  onRecycle(): void {
    super.onRecycle();
    this.$screen.replaceChildren();
  }

  static $styles = [
    css`
      :host {
        display: block;
      }

      /* One row is one line box and columns are content: pre is what keeps a
         captured frame's alignment intact, and the reason no cell arithmetic
         exists anywhere in this file. */
      #screen {
        white-space: pre;
        padding-inline: var(--term-screen-padding-inline, 0px);
        padding-block: var(--term-screen-padding-block, 0px);
      }

      /* A band that follows an unbanded row can take air above it, exactly as
         the replay spaces its command blocks; defaults to none because a
         FRAME is contiguous — the gap is a transcript affordance. */
      .line:not(.banded) + .line.banded {
        margin-block-start: var(--term-screen-band-gap, 0px);
      }
    `,
  ];

  static $html = html`<div id="screen"></div>`;
}

export namespace TermScreen {
  export interface ViewModel {
    /** One finished ANSI string per terminal row — a PaneSlot.lines payload. */
    lines: string[];
  }
}
